import React from 'react';
import { Bell, Loader2, Mail, Phone } from 'lucide-react';
import Button from '../../shared/Button';
import StatusBadge from './LeadStatusBadge';

const REMINDER_FILTERS = [
  { key: 'due_today', label: 'Due today' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'all', label: 'All open' },
  { key: 'completed', label: 'Completed' },
];

const formatDateTime = (d) =>
  d
    ? new Date(d).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

/**
 * Shared Today's follow-ups for Internal + External lead pages.
 * leadFromReminder(r) → lead object; nameFromLead(lead) → display name
 */
const LeadFollowUpsPanel = ({
  reminders = [],
  isLoading = false,
  filter = 'due_today',
  onFilterChange,
  onOpenLead,
  onComplete,
  isCompleting = false,
  emptyHint = 'No reminders in this view. Follow-ups appear after a meeting is marked done.',
  leadFromReminder = (r) => r.consultation || r.lead,
  nameFromLead = (lead) =>
    lead?.guest_name ||
    lead?.client_display_name ||
    lead?.client?.name ||
    lead?.client_name ||
    'Lead',
  emailFromLead = (lead) =>
    lead?.guest_email || lead?.client?.email || lead?.client_email || lead?.client_display_email,
  phoneFromLead = (lead) =>
    lead?.guest_phone || lead?.client?.phone || lead?.client_phone || lead?.client_display_phone,
}) => (
  <div className="product-panel overflow-hidden">
    <div className="border-b border-border px-4 py-3 sm:px-5 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <Bell className="w-4 h-4 text-ink" />
        <h2 className="text-sm font-semibold text-ink">Today&apos;s follow-ups</h2>
        {reminders.length > 0 && filter === 'due_today' && (
          <span className="text-xs font-medium text-ink-secondary bg-surface-muted border border-border px-2 py-0.5 rounded-full">
            {reminders.length} action{reminders.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <div className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border">
        {REMINDER_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => onFilterChange?.(f.key)}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
              filter === f.key
                ? 'bg-surface text-ink shadow-sm'
                : 'text-ink-secondary hover:text-gray-900'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>
    </div>
    <div className="p-4 sm:p-5">
      {isLoading ? (
        <div className="py-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-ink mx-auto" />
        </div>
      ) : reminders.length === 0 ? (
        <p className="text-sm text-ink-tertiary text-center py-6">{emptyHint}</p>
      ) : (
        <div className="space-y-3">
          {reminders.map((r) => {
            const lead = leadFromReminder(r);
            const name = nameFromLead(lead);
            return (
              <div
                key={r.id}
                className="product-surface-muted p-3 sm:p-4 flex flex-col sm:flex-row sm:items-start gap-3"
              >
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-ink">{r.title}</p>
                    <StatusBadge status={r.status} label={r.status} />
                  </div>
                  <p className="text-xs text-ink-secondary leading-relaxed">{r.body}</p>
                  <p className="text-[11px] text-ink-tertiary">
                    Due {formatDateTime(r.due_at)}
                    {lead?.status_meta?.label ? ` · Lead: ${lead.status_meta.label}` : ''}
                    {name ? ` · ${name}` : ''}
                  </p>
                  {Array.isArray(r.recommended_checklist) && r.recommended_checklist.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-xs text-ink-secondary">
                      {r.recommended_checklist.slice(0, 4).map((item) => (
                        <li key={item} className="flex gap-1.5">
                          <span className="text-ink-tertiary">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {r.recommended_action === 'send_email' && (
                    <Button
                      size="sm"
                      variant="primary"
                      className="!rounded-lg"
                      loading={isCompleting}
                      onClick={() => {
                        const email = emailFromLead(lead);
                        if (email) {
                          const subject = encodeURIComponent(`Following up — ${name}`);
                          window.open(`mailto:${email}?subject=${subject}`, '_blank');
                        }
                        onComplete?.(r.id, { action: 'send_email' });
                      }}
                    >
                      <Mail className="w-3.5 h-3.5 mr-1" />
                      Send email
                    </Button>
                  )}
                  {r.recommended_action === 'call_client' && (
                    <Button
                      size="sm"
                      variant="primary"
                      className="!rounded-lg"
                      onClick={() => {
                        const phone = phoneFromLead(lead);
                        if (phone) window.open(`tel:${phone}`);
                        if (lead) onOpenLead?.(lead);
                      }}
                    >
                      <Phone className="w-3.5 h-3.5 mr-1" />
                      Call client
                    </Button>
                  )}
                  {lead && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="!rounded-lg"
                      onClick={() => onOpenLead?.(lead)}
                    >
                      Open lead
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="!rounded-lg text-ink-tertiary"
                    onClick={() => onComplete?.(r.id, { action: 'dismiss' })}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
);

export { REMINDER_FILTERS };
export default LeadFollowUpsPanel;
